const { fetchWithRetry } = require("./utils");

// Function to create a new user  
const createUser = async (userData) => {
    try {
        const response = await fetch('/api/users', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData),
            credentials: 'include', // Include cookies in the request  
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Error creating user: ${errorData.error}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating user:', error);
        throw error;
    }
};

// Function to login a user  
const loginUser = async (email, password, province) => {
    try {
        const response = await fetch('/api/users/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
            credentials: 'include', // Include cookies in the request  
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Error logging in: ${errorData.error}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error logging in:', error);
        throw error;
    }
};

// Function to logout a user  
const logoutUser = async () => {
    try {
        const response = await fetchWithRetry('/api/users/logout', {
            method: 'POST',
        });

        if (!response.ok) {
            throw new Error(`Error logging out: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error logging out:', error);
        throw error;
    }
};

// Function to verify OTP  
const verifyOtp = async (email, otp) => {
    try {
        const response = await fetchWithRetry('/api/users/verify-otp', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, otp }),
        });

        if (!response.ok) {
            throw new Error(`Error verifying OTP: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error verifying OTP:', error);
        throw error;
    }
};

//  Function to check if contestants exist
const checkContestantExists = async (userId) => {
    try {
        const response = await fetchWithRetry('/api/users/checkContestantExists', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId }),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Error checking contestants: ${errorData.error}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error checking contestants: ', error);
        throw error;
    }
};

const requestPasswordReset = async (email, url) => {
    try {
        const response = await fetch('/api/users/requestPasswordReset', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, url }),
            credentials: 'include',
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Error sending password reset email: ${errorData.error}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error sending password reset email:', error);
        throw error;
    }
};

const resetPassword = async (id, token, password) => {
    try {
        const response = await fetch('/api/users/resetPassword', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id, token, password }),
            credentials: 'include',
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Error resetting password: ${errorData.error}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error resetting password:', error);
        throw error;
    }
};

const uploadProfilePhoto = async (contestantId, file) => {  
    try {  
        const formData = new FormData();  
        formData.append('profilePhoto', file);  
  
        const response = await fetch(`/api/contestants/${contestantId}/profilePhoto`, {  
            method: 'POST',  
            body: formData,  
            credentials: 'include',
        });  
  
        if (!response.ok) {  
            const errorData = await response.json();  
            throw new Error(`Error uploading profile photo: ${errorData.error}`);  
        }  
  
        return await response.json();  
    } catch (error) {  
        console.error('Error uploading profile photo:', error);  
        throw error;  
    }  
};  

const fetchProfilePhoto = async (contestantId) => {  
    try {  
        const response = await fetch(`/api/contestants/${contestantId}/profilePhoto`, {  
            method: 'GET',  
            credentials: 'include', // Include credentials if needed for authentication  
        });  
  
        if (!response.ok) {  
            const errorData = await response.json();  
            throw new Error(`Error fetching profile photo: ${errorData.error}`);  
        }  
  
        // We assume the response is an image, so we convert it to a blob  
        const blob = await response.blob();  
          
        // Create an object URL for the image blob  
        const imageUrl = URL.createObjectURL(blob);  
          
        return imageUrl; // Return the URL to be used in an <img> tag or similar  
    } catch (error) {  
        console.error('Error fetching profile photo:', error);  
        throw error;  
    }  
};  

const markApplicationComplete = async (userId) => {  
    try {  
        const response = await fetchWithRetry(`/api/users/${userId}/applicationCompleted`, {  
            method: 'PATCH',  
            headers: {  
                'Content-Type': 'application/json', // Set the content type to JSON  
            },  
            credentials: 'include', // Include credentials if needed for authentication  
        });  
  
        if (!response.ok) {  
            const errorData = await response.json();  
            throw new Error(`Error marking application complete: ${errorData.error}`);  
        }  
  
        const updatedUser = await response.json(); // Parse the JSON response  
        return updatedUser; // Return the updated user object  
  
    } catch (error) {  
        console.error('Error marking application complete:', error);  
        throw error;  
    }  
};  

const updateActiveProvince = async (userId, provinceId) => {  
    try {  
        const token = localStorage.getItem('token');  
        const response = await fetch(`/api/users/${userId}/setActiveProvince`, {  
            method: 'PATCH',  
            headers: {  
                'Content-Type': 'application/json',  
                'Authorization': `Bearer ${token}`  
            },  
            body: JSON.stringify({ provinceId })  
        });  
        const data = await response.json();  
        if (!response.ok) {  
            throw new Error(data.error || 'Failed to update active province');  
        }  
        return data;  
    } catch (error) {  
        throw error;  
    }  
};  

const sendEligibilityNotification = async (teacherName, teacherEmail, memberNames = []) => {
  try {
    const response = await fetch("/api/users/eligibilityNotification", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: teacherName,
        email: teacherEmail,
        memberNames,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to send eligibility notification");
    }

    return data;
  } catch (error) {
    throw error;
  }
};

module.exports = {
    createUser,
    loginUser,
    logoutUser,
    verifyOtp,
    checkContestantExists,
    requestPasswordReset,
    resetPassword,
    uploadProfilePhoto,
    fetchProfilePhoto,
    markApplicationComplete,
    updateActiveProvince,
    sendEligibilityNotification
};  

// Fetch users by role (admin only)
const getUsersByRole = async (role) => {
    try {
        const response = await fetch(`/api/users/role/${role}`, {
            method: 'GET',
            credentials: 'include'
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to fetch users');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching users by role:', error);
        throw error;
    }
};

const sendGradingSheet = async (applicationId, judgeIds) => {
    try {
        const response = await fetch('/api/users/sendGradingSheet', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ applicationId, judgeIds })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to send grading sheet');
        }
        return await response.json();
    } catch (error) {
        console.error('Error sending grading sheet:', error);
        throw error;
    }
};

module.exports.getUsersByRole = getUsersByRole;
module.exports.sendGradingSheet = sendGradingSheet;

// Get the current grading assignment for the authenticated user
const getCurrentGrading = async () => {
    try {
        const response = await fetch('/api/users/currentGrading', {
            method: 'GET',
            credentials: 'include'
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to fetch current grading');
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching current grading:', error);
        throw error;
    }
};

module.exports.getCurrentGrading = getCurrentGrading;